<ng-container *transloco="let t; prefix: 'organizations.detailPage.contractsTab'">
  <!-- A reveal toggle re-fetches only the contracts, so the heading row hosting those checkboxes stays
       mounted while the list below it swaps to the loader; unmounting a checkbox mid-interaction would
       make it vanish under the pointer. Everything outside the contracts list is untouched by a toggle. -->
  <header class="b-contracts-intro">
    <h2 class="b-contracts-intro__title">{{ t('title') }}</h2>
    <p class="b-contracts-intro__subtitle">{{ t('subtitle') }}</p>
  </header>

  @if (!loading()) {
    <section class="b-section">
      <h3 class="b-section__heading">
        {{ t('availableFeatures.title') }}
        <span class="b-count">{{ availableWorkItemTypes().length }}</span>
      </h3>

      <p class="b-legend">
        <span class="b-legend__item b-legend__item--orderable">{{ t('legend.orderable') }}</span>
        <span class="b-legend__item b-legend__item--feature">{{ t('legend.feature') }}</span>
      </p>

      @if (availableWorkItemTypes().length > 0) {
        <div class="b-available-types">
          @for (workItemType of availableWorkItemTypes(); track workItemType.id) {
            <nz-tag
              class="b-wit-pill b-available-types__item"
              [class.b-wit-pill--feature]="isFeatureWorkItemType(workItemType.sort)"
              [title]="isFeatureWorkItemType(workItemType.sort) ? t('legend.feature') : t('legend.orderable')"
            >
              {{ labels.getWorkItemTypeLabel(workItemType) }}
            </nz-tag>
          }
        </div>
      } @else {
        <p class="text-center">{{ t('availableFeatures.empty') }}</p>
      }
    </section>
  }

  <section class="b-section">
    <div class="b-section__heading-row">
      <h3 class="b-section__heading">
        {{ t('contracts.heading') }}
        @if (!contractsDetailFacade.contractsBusy()) {
          <span class="b-count">{{ contracts().length }}</span>
        }
      </h3>
    </div>

    <div class="b-contracts__toolbar">
      <div class="b-contracts__reveal">
        <label
          nz-checkbox
          [ngModel]="contractsDetailFacade.showInactiveContracts()"
          [nzDisabled]="contractsDetailFacade.contractsBusy()"
          (ngModelChange)="contractsDetailFacade.setShowInactiveContracts($event)"
        >
          {{ t('contracts.reveal.showExpiredContracts') }}
        </label>
        <label
          nz-checkbox
          [ngModel]="contractsDetailFacade.showDeletedLineItems()"
          [nzDisabled]="contractsDetailFacade.contractsBusy()"
          (ngModelChange)="contractsDetailFacade.setShowDeletedLineItems($event)"
        >
          {{ t('contracts.reveal.showDeletedLineItems') }}
        </label>
      </div>

      <div class="b-contracts__toolbar-actions">
        <aml-button
          classList="icon-button-muted"
          [variant]="ButtonVariantMap.subtle"
          [iconOnly]="true"
          [icon]="IconNameMap.chevronDoubleDown"
          [disabled]="contractsDetailFacade.contractsBusy()"
          [attr.aria-label]="t('contracts.expandAll')"
          nz-tooltip
          [nzTooltipTitle]="t('contracts.expandAll')"
          (click)="contractsDetailFacade.expandAll()"
        />
        <aml-button
          classList="icon-button-muted"
          [variant]="ButtonVariantMap.subtle"
          [iconOnly]="true"
          [icon]="IconNameMap.chevronDoubleUp"
          [disabled]="contractsDetailFacade.contractsBusy()"
          [attr.aria-label]="t('contracts.collapseAll')"
          nz-tooltip
          [nzTooltipTitle]="t('contracts.collapseAll')"
          (click)="contractsDetailFacade.collapseAll()"
        />
        @if (contractsDetailFacade.canSync()) {
          <aml-button
            class="b-contracts__sync"
            [variant]="ButtonVariantMap.secondary"
            [icon]="contractsDetailFacade.syncing() ? IconNameMap.loader : IconNameMap.rotateRight"
            [disabled]="contractsDetailFacade.syncing() || contractsDetailFacade.contractsBusy()"
            (click)="contractsDetailFacade.syncFromSalesforce(organizationId())"
          >
            {{ t('sync.label') }}
          </aml-button>
        }
      </div>
    </div>

    @if (contractsDetailFacade.contractsBusy()) {
      <div class="b-loader">
        <aml-loader [isLoading]="true" />
      </div>
    } @else {
      @if (contracts().length > 0) {
        <nz-collapse class="b-contracts__panels">
          @for (contract of contracts(); track contract.id) {
            <nz-collapse-panel
              [nzHeader]="contractHeader"
              [nzActive]="contractsDetailFacade.isExpanded(contract.id)"
              [class.b-contracts__panel--inactive]="!contract.isActive"
              (nzActiveChange)="contractsDetailFacade.onExpandChange(contract.id, $event)"
            >
              <ng-template #contractHeader>
                <span class="b-contract-header">
                  <span class="b-contract-header__name">{{ contract.displayName }}</span>
                  <span class="b-contract-header__dates">{{ labels.getContractDateRange(contract) }}</span>
                  @if (!contract.isActive) {
                    <nz-tag class="b-contract-header__state {{ labels.getContractStateClass(contract) }}">
                      {{ labels.getContractStateLabel(contract) }}
                    </nz-tag>
                  } @else if (contract.endDate) {
                    @if (isExpiringSoon(contract)) {
                      <nz-tag class="b-contract-header__expiring">
                        <aml-icon [iconName]="IconNameMap.triangleExclamation" [size]="IconSizeMap.xs" />
                        <span>{{ t('contracts.expiringSoon.label') }}</span>
                        <span aria-hidden="true">·</span>
                        <span>{{ t('contracts.expiringSoon.daysLeft', { days: getDaysLeft(contract) }) }}</span>
                      </nz-tag>
                    } @else {
                      <span class="b-contract-header__pill">
                        {{ t('contracts.monthsLeft', { months: getMonthsLeft(contract) }) }}
                      </span>
                    }
                  }
                  @if (contract.externalContractLink) {
                    <a
                      class="b-contract-header__sf-link"
                      [href]="contract.externalContractLink"
                      target="_blank"
                      rel="noopener noreferrer"
                      (click)="$event.stopPropagation()"
                    >
                      {{ t('contracts.salesforceLink') }}
                    </a>
                  }
                </span>
              </ng-template>

              <nz-table
                #lineItemsTable
                [nzData]="contract.lineItems"
                [nzShowPagination]="false"
                [nzFrontPagination]="false"
                nzSize="small"
              >
                <thead>
                  <tr>
                    <th class="b-line-items__index-col">{{ t('contracts.columns.index') }}</th>
                    <th>{{ t('contracts.columns.lineItem') }}</th>
                    <th class="b-line-items__quantity-col">{{ t('contracts.columns.quantity') }}</th>
                    <th>{{ t('contracts.columns.feature') }}</th>
                    <th>{{ t('contracts.columns.links') }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (lineItem of lineItemsTable.data; track lineItem.id; let index = $index) {
                    @let isDeleted = labels.isDeletedLineItem(lineItem);
                    <tr [class.b-line-items__row--deleted]="isDeleted">
                      <td>{{ index + 1 }}</td>
                      <td>
                        <!-- Tag first: the cell truncates with an ellipsis, so a long line item name
                             would push the marker out of view and leave the row marked by colour alone. -->
                        @if (isDeleted) {
                          <nz-tag class="b-line-items__deleted status-neutral">
                            {{ t('contracts.deletedLineItem') }}
                          </nz-tag>
                        }
                        {{ lineItem.displayName }}
                      </td>
                      <td class="b-line-items__quantity-col">{{ lineItem.quantity ?? '–' }}</td>
                      <td>
                        @for (workItemType of lineItem.matchedWorkItemTypes; track workItemType.id) {
                          <nz-tag
                            class="b-wit-pill"
                            [class.b-wit-pill--feature]="isFeatureWorkItemType(workItemType.sort)"
                            [title]="
                              isFeatureWorkItemType(workItemType.sort) ? t('legend.feature') : t('legend.orderable')
                            "
                          >
                            {{ labels.getWorkItemTypeLabel(workItemType) }}
                          </nz-tag>
                        } @empty {
                          <span class="b-muted">{{ t('contracts.noMatchedFeature') }}</span>
                        }
                      </td>
                      <td>
                        <span class="b-link-pills">
                          @let links =
                            [
                              { href: lineItem.externalProductLink, labelKey: 'contracts.productLink' },
                              { href: lineItem.externalLineItemLink, labelKey: 'contracts.lineItemLink' },
                            ];

                          @for (link of links; track link.labelKey) {
                            @if (link.href) {
                              <a
                                class="b-link-pill"
                                [href]="link.href"
                                target="_blank"
                                rel="noopener noreferrer"
                                (click)="$event.stopPropagation()"
                              >
                                <aml-icon [iconName]="IconNameMap.arrowUpRightFromSquare" [size]="IconSizeMap.xs" />
                                {{ t(link.labelKey) }}
                              </a>
                            }
                          }
                        </span>
                      </td>
                    </tr>
                  }
                </tbody>
              </nz-table>
            </nz-collapse-panel>
          }
        </nz-collapse>
      } @else {
        <p class="text-center">{{ t('contracts.noContracts') }}</p>
      }
    }
  </section>
</ng-container>
